/* eslint-disable complexity */

import React from 'react';
import { useTranslations } from '@unisporkal/localization';
import DropdownField from '../../Controls/DropdownField';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex.ts';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import styles from './MultipleAgreements.module.scss';

const MultipleAgreements = () => {
  const t = useTranslations();
  const {
    response: { config: { options, userHasMultiplePaAgreements } = {} } = {},
  } = useOnLoad();
  const { selectedAgreementOptions, selectedOption, setSelectedOptionIndex } =
    useSelectedIndex();
  const selectedOptionType = selectedOption?.type;
  const hasMultipleAgreementOptions = selectedAgreementOptions?.length > 1;

  // The dropdown's <option value> entries are indices into the FILTERED
  // selectedAgreementOptions list, but selectedOptionIndex is a GLOBAL index
  // into options. Use the filtered position of the selected option so the
  // displayed pack matches the agreement driving the modified sizes — otherwise
  // the select shows the wrong pack and re-picking the displayed value fires no
  // change event.
  const selectedAgreementIndex = Math.max(
    selectedAgreementOptions.findIndex(
      (option) => option.id === selectedOption?.id
    ),
    0
  );

  const agreementsTitle =
    selectedOptionType === 'ImagePack'
      ? t('select_ultrapack')
      : t('select_agreement');

  const handleOnChange = (e) => {
    const newSelectedOption =
      selectedAgreementOptions[parseInt(e.target.value, 10)];
    options.forEach((option, index) => {
      if (newSelectedOption.id === option.id) {
        setSelectedOptionIndex(index);
      }
    });
  };

  const agreementOptionLabel = (option) => {
    if (selectedOptionType === 'PremiumAccess') {
      if (option.effectivePermissions?.indexOf('Download') === -1) {
        return t('agreement_name_view_only', {
          agreement_name: option.friendlyName,
        });
      }
      return option.friendlyName;
    }
    const { downloadCapacity, downloadCount } = option;
    const downloadsRemaining = downloadCapacity - downloadCount;
    const downloadsRemainingLabel = t('agreement_details_downloads', {
      download_remaining: downloadsRemaining,
      download_capacity: downloadCapacity,
    });
    return `${t(`${option.tier}_resolution_pack`)} ${downloadsRemainingLabel}`;
  };

  const renderAgreementOptions = () =>
    selectedAgreementOptions.map((option, index) => (
      <option
        value={index}
        key={option.id}
      >
        {agreementOptionLabel(option)}
      </option>
    ));

  if (!selectedOption) {
    return null;
  }

  if (!hasMultipleAgreementOptions && userHasMultiplePaAgreements) {
    return (
      <section
        className={styles.container}
        data-testid="multiple-pa-agreements"
      >
        <div>{selectedOption?.friendlyName}</div>
      </section>
    );
  }

  if (!hasMultipleAgreementOptions) {
    return null;
  }

  return (
    <section
      className={styles.container}
      data-testid="multiple-agreements"
    >
      <label
        className={styles.header}
        htmlFor={agreementsTitle}
        data-testid="label"
      >
        {agreementsTitle}
      </label>
      <DropdownField
        defaultValue={selectedAgreementIndex}
        dropdownClass={styles.blackText}
        id={agreementsTitle}
        onChange={handleOnChange}
        required
      >
        {renderAgreementOptions()}
      </DropdownField>
    </section>
  );
};

export default MultipleAgreements;
